import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError, api, unwrap, type Schemas } from '../api/client.js';
import { errorMessage } from '../errors.js';
import { formatDate } from '../i18n.js';
import { useMe } from '../session.js';
import { Button, Card, Notice, Segmented } from './ui.js';

type Method = 'dns_txt' | 'meta_tag' | 'file';
const METHODS: Method[] = ['meta_tag', 'dns_txt', 'file'];
/** Step-by-step guides for the hosts and systems most used in Germany (T-26). */
const GUIDES: Record<Method, string[]> = {
  meta_tag: ['wordpress', 'jimdo', 'other'],
  dns_txt: ['ionos', 'strato', 'allinkl', 'other'],
  file: ['ftp', 'other'],
};

/** A value to put on the site, in full (it may be typed by hand), with its copy button. */
function Copy({ label, value, named }: { label: string; value: string; named: boolean }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  return (
    <div className="copy">
      {/* Two values (DNS name and value, file path and content) need their names. */}
      {named ? <span className="label">{label}</span> : null}
      <div className="code">
        <span>{value}</span>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            // Refused copying changes nothing: the value stands in full beside the button.
            void navigator.clipboard?.writeText(value).then(
              () => setCopied(true),
              () => undefined,
            );
          }}
          aria-label={t('verify.copyLabel', { label })}
        >
          {copied ? t('verify.copied') : t('verify.copy')}
        </Button>
      </div>
    </div>
  );
}

/** "Anleitung für Ihren Anbieter": the method's providers as chips, the steps of the chosen one. */
function Guides({ method }: { method: Method }) {
  const { t } = useTranslation();
  const [guide, setGuide] = useState(GUIDES[method][0] ?? 'other');
  return (
    <details>
      <summary>{t('verify.guide')}</summary>
      <div className="body">
        <div className="chips" role="group" aria-label={t('verify.guide')}>
          {GUIDES[method].map((g) => (
            <button key={g} type="button" aria-pressed={g === guide} onClick={() => setGuide(g)}>
              {t(`verify.guides.${g}.title`)}
            </button>
          ))}
        </div>
        <ol>
          {(t(`verify.guides.${guide}.steps`, { returnObjects: true }) as string[]).map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      </div>
    </details>
  );
}

/**
 * Ownership check in the site settings (architecture §10.2, site-settings.html #verify): opens the
 * weaknesses' details (T-101) and lifts the 25-page limit. Once confirmed, its date (decision 4 of
 * SP-UX10.5: from `verifiedAt`, without the method).
 */
export function Verification({ site }: { site: Schemas['SiteDetail'] }) {
  const { t, i18n } = useTranslation();
  const timeZone = useMe().data?.account.timezone;
  const client = useQueryClient();
  const [chosen, setChosen] = useState<Method | null>(null);
  const status = useQuery({
    queryKey: ['site', site.id, 'verification'],
    queryFn: () =>
      unwrap(api.GET('/sites/{siteId}/verification', { params: { path: { siteId: site.id } } })),
  });
  const active = status.data?.activeChallenge;
  // The method of a code from before, until another one is chosen.
  const method = chosen ?? active?.method ?? 'meta_tag';
  const create = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/sites/{siteId}/verification-challenges', {
          params: { path: { siteId: site.id } },
          body: { method },
        }),
      ),
    onSuccess: () => {
      verify.reset();
      void status.refetch();
    },
  });
  const verify = useMutation({
    mutationFn: () =>
      unwrap(api.POST('/sites/{siteId}/verify', { params: { path: { siteId: site.id } } })),
    onSuccess: () => client.invalidateQueries({ queryKey: ['site', site.id] }),
  });
  // Linked from a weakness only the confirmed owner may see (Probleme, T-101).
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (window.location.hash === '#verify') heading.current?.scrollIntoView();
  }, []);
  const date = (iso: string) => formatDate(iso, i18n.language, timeZone);

  const title = (
    <h2 ref={heading} id="verify">
      {t('settings.verification.title')} <small>{t('settings.verification.subtitle')}</small>
    </h2>
  );

  if (site.verified || verify.isSuccess) {
    const verifiedAt = verify.data?.verifiedAt ?? status.data?.verifiedAt;
    return (
      <Card>
        {title}
        <Notice kind="success" title={t('settings.verification.done')}>
          {verifiedAt ? t('settings.verification.verifiedAt', { date: date(verifiedAt) }) : null}
        </Notice>
      </Card>
    );
  }

  // The code just made, for the method shown; a code from before is not sent again by the API.
  const c = create.data?.method === method ? create.data : undefined;
  const existing = create.data ?? active;
  const pending = Boolean(existing);
  const hint =
    verify.error instanceof ApiError && verify.error.code === 'NOT_VERIFIED'
      ? t(`verify.hints.${String((verify.error.problem as { hint?: string }).hint)}`, {
          defaultValue: t('verify.hints.unreachable'),
        })
      : verify.error
        ? errorMessage(verify.error, t)
        : null;

  return (
    <Card>
      {title}
      <div className="verif">
        <Segmented
          label={t('verify.method')}
          value={method}
          options={METHODS.map((m) => [m, t(`verify.short.${m}`)] as const)}
          onChange={setChosen}
          className=""
        />
        {c ? (
          <>
            <p className="bodyM">
              {t(`verify.methods.${c.method}.step`)}{' '}
              {t('verify.validUntil', { date: date(c.expiresAt) })}
            </p>
            {c.method === 'dns_txt' ? (
              <>
                <Copy label={t('verify.dnsName')} value={c.instructions.dnsRecordName} named />
                <Copy label={t('verify.dnsValue')} value={c.instructions.dnsRecordValue} named />
              </>
            ) : null}
            {c.method === 'meta_tag' ? (
              <Copy label={t('verify.metaTag')} value={c.instructions.metaTag} named={false} />
            ) : null}
            {c.method === 'file' ? (
              <>
                <Copy label={t('verify.filePath')} value={c.instructions.filePath} named />
                <Copy label={t('verify.fileContent')} value={c.instructions.fileContent} named />
              </>
            ) : null}
          </>
        ) : (
          <p className="bodyM">{t(`verify.methods.${method}.text`)}</p>
        )}
        {/* A code of another method than the one shown: that one is checked (review #187). */}
        {existing && !c ? (
          <p className="bodyM">
            {t('verify.activeCode', {
              method: t(`verify.methods.${existing.method}.title`),
              date: date(existing.expiresAt),
            })}
          </p>
        ) : null}
        {/* Sheet: "Jetzt prüfen" first once there is a code, a new code beside it. */}
        <div className="acts">
          {pending ? (
            <Button size="sm" busy={verify.isPending} onClick={() => verify.mutate()}>
              {t('verify.check')}
            </Button>
          ) : null}
          <Button
            size="sm"
            variant={pending ? 'ghost' : 'primary'}
            busy={create.isPending}
            onClick={() => create.mutate()}
          >
            {pending ? t('verify.newCode') : t('verify.createCode')}
          </Button>
        </div>
        {create.isError ? <Notice kind="error">{errorMessage(create.error, t)}</Notice> : null}
        {hint ? <Notice kind="error">{hint}</Notice> : null}
        {c ? <Guides key={c.method} method={c.method} /> : null}
      </div>
    </Card>
  );
}
